
/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareEntity } from '../fw/MetaDataHelper';
import * as Permission from '../util/Permission';
import {RequiredPayPlans} from './OrganisationType';
import countries from '../../common/intl/messages/countries.en';
import config from '../../common/config';
import { FLOW_DEF_VERSION_ID_DEVELOPMENT } from './FlowDefVersionType';

export const FILTER_TYPE_NONE = 'none';
export const FILTER_TYPE_SINGLE = 'single';
export const FILTER_TYPE_MULTI = 'multi';
export const FILTER_TYPE_FULL = 'full';

export const AUTH_TYPE_GLOBAL = "global";
export const AUTH_TYPE_PER_USER = "per-user";
export const AUTH_TYPE_NONE = "none";

export const SYNC_INCREMENTAL = "incremental";
export const SYNC_FULL = "full";
export const SYNC_ALWAYS_ALL = "always-all";

/* configuration of type of sync options (pagerOptions) */
export const INCREMENTAL_DISABLED = "disabled";
export const INCREMENTAL_YES = "incremental";
export const INCREMENTAL_FULL_CONTENT = "full-content";



export let channelTags = {
    "crm": {
        "name": "CRM",
    },
    "invoicing": {
        "name": "Invoicing"
    },
    "accounting": {
        "name": "Accounting"
    },
    "erp": {
        "name": "ERP"
    },
    "pos": {
        "name": "Point Of Sale (POS)"
    },
    "eshop": {
        "name": "e-Shop"
    },
    "warehouse": {
        "name": "Warehouse management"
    },
    "telephony": {
        "name": "Telephony"
    },
    "mail": {
        "name": "Mailing (Mass+Transactional)"
    },
    "webmail": {
        "name": "WebMail or Mail Client"
    },
    "projects": {
        "name": "Project management"
    },
    "marketing": {
        "name": "Marketing"
    },
    "timetracking": {
        "name": "Time tracking"
    },
    "task": {
        "name": "Task / Issue / Bug"
    },
    "support": {
        "name": "Support / Helpdesk"
    },
    "api": {
        "name": "API"
    },
    "email-validation": {
        "name": "Email validation"
    },
    "phone-validation": {
        "name": "Phone validation"
    },
    "cnam": {
        "name": "Caller Identification (CNAM)"
    },
    "vat-validation": {
        "name": "VAT number validation"
    },
    "geolocation": {
        "name": "Geolocation"
    },
    "social": {
        "name": "Social Media"
    },
    "whois": {
        "name": "WHOIS"
    },
    "maps": {
        "name": "Maps"
    },
    "payments": {
        "name": "Payments"
    },
    "dativery": {
        "name": "Dativery"
    }
};

export const channelDefState = {
    "placeholder": {
        name: "Placeholder",
        color: '#158fef',
        priority: 20,
    },
    "draft": {
        name: "Draft",
        color: '#158fef',
        priority: 30,
    },
    "alpha": {
        name: "Alpha",
        color: 'orange',
        priority: 40,
    },
    "beta": {
        name: "Beta",
        color: 'green',
        priority: 50,
    },
    "published": {
        name: "Published",
        hide: true,
        priority: 60,
    },
    "unsupported": {
        name: "Unsupported",
        color: 'blue',
        priority: 10,
    }
};

export const privacyStatus = {
    "no-auth": {
        name: "No authentication needed.",
        priority: 10,
    },
    "by-us": {
        name: "Service is provided by Dativery.",
        priority: 20,
    },
    "your-service-or-us": {
        name: "Provided by Dativery or you can provide your own authentication info.",
        priority: 30,
    },
    "your-service": {
        name: "Your authentication info needed.",
        priority: 40,
    },
};

export const PARSER_GENERIC_JSON = "generic-json";
export const PARSER_MT940 = "mt940";
export const PARSER_GENERIC_XML = "generic-xml";
export const PARSER_GENERIC_CSV = "generic-csv";
export const PARSER_GENERIC_XLS = "generic-xls";
export const PARSER_BINARY_WRAPPER = "binary-wrapper";

export const FETCHER_NONE = "none";
export const FETCHER_IMAP = "imap";
export const FETCHER_GRAPHQL = "graphql";
export const FETCHER_GENERIC = "generic";

export const DEFAULT_PROXY_SERVER = "http://proxy.dativery.com:33129";
export const PROXY_SERVERS = {
    ["http://proxy.dativery.com:33129"]: {
        name: "proxy.dativery.com:33129"
    },
    ["https://proxy.dativery.com:33128"]: {
        name: "proxy.dativery.com:33128"
    },
};

export const PARSERS = {
    [PARSER_GENERIC_JSON]: {
        name: "Generic JSON"
    },
    [PARSER_GENERIC_XML]: {
        name: "Generic XML"
    },
    [PARSER_GENERIC_CSV]: {
        name: "Generic CSV"
    },
    [PARSER_GENERIC_XLS]: {
        name: "Generic XLS"
    },
    [PARSER_MT940]: {
        name: "MT940 (Bank Statements)"
    },
    [PARSER_BINARY_WRAPPER]: {
        name: "Wraps binary as {content: XXX}"
    },
};

export const FILTER_BUILDERS = {
    [FILTER_TYPE_NONE]: {
        name: "None",
    },
    [FILTER_TYPE_SINGLE]: {
        name: "Single filter (?field='value')",
    },
    [FILTER_TYPE_MULTI]: {
        name: "Multi filter (?field='value'&field='value2')",
    },
    [FILTER_TYPE_FULL]: {
        name: "Full filter (field = 'value' or field2 = 'value2')",
    },
};

export default declareEntity({
    entity: "channelDefs",
    graphQL: "ChannelDef",
    name: "Application Definition",
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    defaultOrder: ['name', 'type'],
    isPublic: true,
    defaultTitle: (data) => {
        return data['name'];
    },
    fields: (types) => { return {
        id: {
            name: "Application Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            isPublic: true,
            required: true,
            userEditable: false,
        },
        name: {
            name: "Name",
            trim: true,
            type: "string",
            isPublic: true,
            translatable: true,
            filterable: true,
            defaultValue: "",
        },
        defaultName: {
            name: "Default name",
            type: "string",
            defaultValue: "",
        },
        keywords: {
            name: "Keywords",
            trim: true,
            type: "string",
            isPublic: true,
            translatable: true,
            filterable: true,
            defaultValue: "",
        },
        channelDefVersion: {
            name: "Channel Definition Version",
            to: () => types['channelDefVersions'],
            type: "relation",
            defaultValue: (flow) => {
                return flow['channelDefVersion_id'] || FLOW_DEF_VERSION_ID_DEVELOPMENT;
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        authCheckPeriod: {
            name: "Auth check period",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
            addonAfter: "hours",
        },
        swaggerEnabled: {
            name: "Swagger Enabled",
            type: "boolean",
            defaultValue: false,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        apiVersion: {
            name: "API Version",
            type: "string",
            defaultValue: "",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        type: {
            name: "Type",
            trim: true,
            type: "string",
            isPublic: true,
            defaultValue: "",
            validations: {
                matchRegexp: /^[a-zA-Z0-9\.\-_]+$/,
            }
        },
        urlFragment: {
            name: "URL Fragment",
            trim: true,
            filterable: true,
            type: "string",
            defaultValue: "",
            isPublic: true,
            validations: {
                matchRegexp: /^[a-zA-Z0-9\-]+$/,
            }
        },
        aliases: {
            name: "Aliases",
            description: "Aliases for channel name separated by comma (,).",
            trim: true,
            translatable: true,
            isPublic: true,
            type: "string",
            defaultValue: "",
        },
        rateLimit: {
            name: "Rate Limit",
            description: "Rate limit in format: user=1000/1m, global=1000/1h, 1000/1d (global=per all users, user=per user (default))",
            type: "string",
            defaultValue: "",
        },
        typos: {
            name: "Typos",
            description: "Typos for channel name separated by comma (,).",
            trim: true,
            translatable: true,
            isPublic: true,
            type: "string",
            defaultValue: "",
        },
        connectRequirement: {
            name: "Connect Requirement",
            description: "Add requirements for user to allow connection to application.",
            translatable: true,
            isPublic: true,
            type: "html",
            defaultValue: "",
        },
        tags: {
            name: "Tags",
            type: "enums",
            isPublic: true,
            defaultValue: [],
            values: channelTags
        },
        availableForCountries: {
            name: "Available for countries",
            type: "enums",
            isPublic: true,
            defaultValue: [],
            values: countries
        },
        state: {
            name: "State",
            type: "enum",
            filterable: true,
            isPublic: true,
            defaultValue: "draft",
            values: channelDefState
        },
        visibility: {
            name: "Visibility",
            type: "enum",
            filterable: true,
            isPublic: true,
            defaultValue: "private",
            values: {
                "private": {
                    name: "Private",
                },
                "unlisted": {
                    name: "Unlisted",
                },
                "public": {
                    name: "Public",
                },
            }
        },
        usageDataSource: {
            name: "Use as Data Source",
            type: "boolean",
            filterable: true,
            isPublic: true,
            defaultValue: true,
        },
        usageView: {
            name: "Use type as View",
            type: "boolean",
            filterable: true,
            isPublic: true,
            defaultValue: true,
        },
        usageWriter: {
            name: "Use type as Writer",
            type: "boolean",
            filterable: true,
            isPublic: true,
            defaultValue: false,
        },
        originalChannelDefVersion: {
            name: "Original Channel Definition Version",
            to: () => types['channelDefVersions'],
            type: "relation",
            export: false,
            userEditable: false,
            permission: Permission.PERM_DEVELOPER,
        },
        hasChange: {
            name: "Has Change",
            type: "boolean",
            filterable: true,
            export: false,
            import: false,
            userEditable: false,
            isPublic: false,
            permission: Permission.PERM_DEVELOPER,
        },
        virtual: {
            name: "Virtual Application",
            type: "boolean",
            defaultValue: false,
        },
        addType: {
            name: "Addition type",
            type: "enum",
            defaultValue: "add",
            isPublic: true,
            values: {
                "add": {
                    name: "Can be added by user",
                    description: "User can add it to their organisation"
                },
                "none": {
                    name: "Can't be added",
                    description: "User can't add it to organisation"
                },
            }
        },
        listWithoutPanel: {
            name: "Lists without panel",
            type: "boolean",
            defaultValue: true,
            visibility: (data) => data.usageView,
        },
        description: {
            name: "Description",
            type: "text",
            translatable: true,
            isPublic: true,
            defaultValue: ""
        },
        longDescription: {
            name: "Long Description",
            type: "html",
            translatable: true,
            isPublic: true,
            defaultValue: ""
        },
        developersNotes: {
            name: "Developer's Notes",
            type: "text",
            defaultValue: "",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        fetcher: {
            name: "Fetcher",
            type: "enum",
            defaultValue: FETCHER_GENERIC,
            values: {
                [FETCHER_GENERIC]: {
                    name: "Generic REST"
                },
                [FETCHER_GRAPHQL]: {
                    name: "GraphQL"
                },
                [FETCHER_IMAP]: {
                    name: "IMAP (for mails)"
                },
                [FETCHER_NONE]: {
                    name: "None (no data reading) - usually provided by integration. Will disable access to API calls."
                },
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        pager: {
            name: "Pager",
            type: "enum",
            defaultValue: "paging-all",
            values: {
                // "paging-incremental": {
                //     name: "Paging with append incremental"
                // },
                "paging-all": {
                    name: "Paging with fetching all as pages"
                },
                // "list-all": {
                //     name: "Paging with fetching always all"
                // },
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        parser: {
            name: "Parser",
            type: "enum",
            defaultValue: PARSER_GENERIC_JSON,
            values: PARSERS,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        filterBuilder: {
            name: "Filter builder",
            type: "enum",
            defaultValue: FILTER_TYPE_NONE,
            values: FILTER_BUILDERS,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        authType: {
            name: "Authorization Type",
            type: "enum",
            defaultValue: AUTH_TYPE_GLOBAL,
            values: {
                [AUTH_TYPE_GLOBAL]: {
                    name: "Per organisation"
                },
                [AUTH_TYPE_PER_USER]: {
                    name: "Per user in organisation"
                },
                [AUTH_TYPE_NONE]: {
                    name: "None"
                },
            },
            hideInApi: true,
        },
        providedByUs: {
            name: "Provided by Dativery",
            type: "boolean",
            isPublic: true,
            defaultValue: false,
        },
        canBeAddedOnce: {
            name: "Can by added only once",
            type: "boolean",
            isPublic: true,
            defaultValue: false,
        },
        addonRequiredPayPlan: {
            name: "Addon Required Plan",
            type: "enum",
            isPublic: true,
            filterable: true,
            defaultValue: "-",
            values: RequiredPayPlans,
            visibility: (data) => { return data.usageView === true; },
        },
        requiredPayPlan: {
            name: "Required Plan",
            type: "enum",
            isPublic: true,
            filterable: true,
            defaultValue: "-",
            values: RequiredPayPlans,
        },
        requiresCaching: {
            name: "Requires caching",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
            visibility: (data) => { return data['requiredPayPlan'] !== '-' && data['requiredPayPlan'] !== 'plan-2'; },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        cachingDisabled: {
            name: "Caching disabled",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
            userEditable: false,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        extraPayment: {
            name: "Extra Payment",
            type: "boolean",
            isPublic: true,
            defaultValue: false,
        },
        privacy: {
            name: "Privacy",
            type: "enum",
            values: privacyStatus,
            userEditable: false,
            import: false,
            export: false,
            isPublic: true,
            controllerFn: "getPrivacy",
        },
        filterOptions: {
            name: "Filter Options",
            type: "object",
            defaultValue: {},
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        pagerOptions: {
            name: "Pager Options",
            type: "object",
            defaultValue: {},
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        fetcherOptions: {
            name: "Fetcher Options",
            type: "object",
            defaultValue: {},
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        parserOptions: {
            name: "Parser Options",
            type: "object",
            defaultValue: {},
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        version: {
            name: "Version",
            type: "int",
            defaultValue: 1
        },
        versionConfig: {
            name: "Version Config",
            type: "object",
            import: false,
            export: false,
            userEditable: false,
            permission: Permission.PERM_DEVELOPER,
        },
        popularity: {
            name: "Popularity",
            type: "int",
            isPublic: true,
            defaultValue: 1,
            import: false,
            export: false,
        },
        icon: {
            name: "Icon",
            type: "string",
            isPublic: true,
            defaultValue: ""
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        changeAuthor: {
            name: "Last change author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        changeAuthorName: {
            name: "Change Author Name",
            type: "string",
            userEditable: false,
        },
        metadata: {
            name: "Metadata",
            type: "object",
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getMetaData",
        },
        hasOwnPlugin: {
            name: "Works without extension",
            type: "boolean",
            defaultValue: false,
            visibility: (data) => { return data['usageView']; },
        },
        requiresAgent: {
            name: "Requires agent",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
            visibility: (data) => { return data['usageDataSource']; },
        },
        canUseAgent: {
            name: "Can use agent",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
            visibility: (data) => { return data['usageDataSource'] && !data['requiresAgent']; },
        },
        requiresProxy: {
            name: "Requires proxy",
            type: "boolean",
            defaultValue: false,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            visibility: (data) => { return data['usageDataSource'] && !data['requiresAgent'] && !data['canUseAgent']; },
        },
        proxyServer: {
            name: "Proxy server",
            type: "enum",
            defaultValue: "",
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            values: PROXY_SERVERS,
            visibility: (data) => { return data['usageDataSource'] && !data['requiresAgent'] && !data['canUseAgent'] && data['requiresProxy']; },
        },
        deprecated: {
            name: "Is Deprecated?",
            type: "boolean",
            defaultValue: false,
            filterable: true,
            isPublic: true,
        },
        onConfigSave: {
            name: "On Config Save (JS)",
            type: "javascript",
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            globalVariables: {
                'options': 'options: Map',
                'currentUserOptions': 'currentUserOptions: Map',
                'setOptions': 'setOptions(newOptions: Map<string,any>)',
                'setAuthOptions': 'setAuthOptions(newOptions: Map<string,any>)',
                'setCurrentUserOptions': 'setCurrentUserOptions(newOptions: Map<string,any>)',
                'doAction': 'doAction: (args)',
            },
        },
        onReadAppConfig: {
            name: "On Read App Config (JS)",
            type: "javascript",
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            globalVariables: {
                'options': 'options: Map',
                'currentUserOptions': 'currentUserOptions: Map',
                'setOptions': 'setOptions(newOptions: Map<string,any>)',
                'setAuthOptions': 'setAuthOptions(newOptions: Map<string,any>)',
                'setCurrentUserOptions': 'setCurrentUserOptions(newOptions: Map<string,any>)',
                'doAction': 'doAction: (args)',
            },
        },
        onConfigDefault: {
            name: "Before config (JS)",
            type: "javascript",
            hideInApi: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            globalVariables: {
                'options': 'options: Map',
                'currentUserOptions': 'currentUserOptions: Map',
                'setOptions': 'setOptions(newOptions: Map<string,any>)',
                'setAuthOptions': 'setAuthOptions(newOptions: Map<string,any>)',
                'setCurrentUserOptions': 'setCurrentUserOptions(newOptions: Map<string,any>)',
            },
        },
        showContactUs: {
            name: "Show Contact Us",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
        },
        hideAddButton: {
            name: "Hide Add Button",
            type: "boolean",
            defaultValue: false,
            isPublic: true,
        },
        defaultInjection: {
            name: "Default injection",
            description: "Injection that will be used when there is no injection in URL mapping.",
            to: () => types['channelInjectionDefs'],
            type: "relation",
            hideInApi: true,
            relativeTo: ["id"],
            visibility: (data) => { return !data['hasOwnPlugin'] && !data['virtual'] && data['usageView']; },
        },
        backupInjection: {
            name: "Backup injection",
            description: "This injection is used when default injection (or injection in URL mapping) can't be used. If backup injection fails too, the panel is simply appended to bottom",
            to: () => types['channelInjectionDefs'],
            type: "relation",
            hideInApi: true,
            relativeTo: ["id"],
            visibility: (data) => { return !data['hasOwnPlugin'] && !data['virtual'] && data['usageView']; },
        },
        publicWeb: {
            name: "Web",
            type: "string",
            controllerFn: "getPublicWeb",
            isPublic: true,
        },
        developerGroup: {
            name: "Developer",
            type: "relation",
            to: () => types['developerGroups'],
            defaultValue: config.DEVELOPER_DATIVERY,
            filterable: true,
            userEditable: true,
            required: true,
            isPublic: true,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        extendedFrom: {
            name: "Extended from",
            type: "relation",
            to: () => types['channelDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        swaggerUrl: {
            name: "Swagger URL",
            type: "URL",
            visibility: (data) => { return data['swaggerEnabled']; },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
    }; },
    relations: (types) => { return {
        actionDefs: {
            type: 'oneToMany',
            to: types['actionDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        searchActionDefs: {
            type: 'oneToMany',
            to: types['actionDefs'],
            import: false,
            controllerFn: 'getSearchActionDefs',
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        invokeActionDefs: {
            type: 'oneToMany',
            to: types['actionDefs'],
            import: false,
            controllerFn: 'getInvokeActionDefs',
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        sourceDefs: {
            type: 'oneToMany',
            to: types['sourceDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        channelDefVersions: {
            type: 'oneToMany',
            to: types['channelDefVersions'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        fetches: {
            type: 'oneToMany',
            to: types['fetches'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        sourceGroupDefs: {
            type: 'oneToMany',
            to: types['sourceGroupDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        channelErrorDefs: {
            type: 'oneToMany',
            to: types['channelErrorDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        channelURLDefs: {
            type: 'oneToMany',
            to: types['channelURLDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        channelOptionDefs: {
            type: 'oneToMany',
            to: types['channelOptionDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        channelInjectionDefs: {
            type: 'oneToMany',
            to: types['channelInjectionDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        channelDefExclusions: {
            type: 'oneToMany',
            to: types['channelDefExclusions'],
            export: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: () => {
                return {};
            },
        },
        channelRelationDefs: {
            type: 'oneToMany',
            to: types['channelRelationDefs'],
            export: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: () => {
                return {};
            },
        },
        channelTypeConversionDefs: {
            type: 'oneToMany',
            to: types['channelTypeConversionDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        channelMappingDefs: {
            type: 'oneToMany',
            to: types['channelMappingDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        entityDefs: {
            type: 'oneToMany',
            to: types['entityDefs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        channels: {
            type: 'oneToMany',
            to: types['channels'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        integratedApps: {
            type: 'oneToMany',
            to: types['channelDefs'],
            controllerFn: 'getAllIntegratedApps',
            isPublic: true,
        },
        addonDefs: {
            type: 'oneToMany',
            to: types['addonDefs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        channelScriptDefs: {
            type: 'oneToMany',
            to: types['channelScriptDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        dependantEntityViewDefs: {
            type: 'oneToMany',
            to: types['entityViewDefs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "dependsOnChannelDef_id"
                }
            ],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        channelEntityDefs: {
            type: 'oneToMany',
            to: types['channelEntityDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allEntityDefsToAddAsMapping: {
            type: 'oneToMany',
            to: types['entityDefs'],
            controllerFn: 'getAllEntityDefsToAddAsMapping',
        },
        allGroupDefs: {
            type: 'oneToMany',
			description: "",
            to: types['groupDefs'],
            export: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        scenarioDefs2ChannelDefs: {
            type: 'oneToMany',
            to: types['scenarioDefs2ChannelDefs'],
            export: false,
            isPublic: true,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        scenarios2ChannelDefs: {
            type: 'oneToMany',
            to: types['scenarios2ChannelDefs'],
            export: false,
            isPublic: true,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                }
            ]
        },
        relevantScenarios: {
            type: 'oneToMany',
            to: types['scenarios'],
            isPublic: true,
            args: {
                onlyActiveChannels: {
                    type: 'boolean',
                },
                first: {
                    type: 'int'
                },
                requiredPayPlan: {
                    type: 'string'
                },
                nameLike: {
                    type: 'string'
                },
				ruleType: {
					type: 'string'
				},
				channelDefUrlFragments: {
					type: 'stringArray'
				}
            },
            controllerFn: 'getRelevantScenarios',
        },
        logger: {
            type: 'oneToMany',
            to: types['loggers'],
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                },
            ]
        },
        channelErrors: {
            type: 'oneToMany',
            to: types['channelErrors'],
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                },
            ]
        },
        notifications: {
            type: 'oneToMany',
            to: types['organisationNotifications'],
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "channelDef_id"
                },
            ]
        },
    }; }
});
